import { useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, Gamepad2, Youtube, Check, Loader2, ArrowRight, ShieldCheck, Sparkles, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import DiscordCta from "@/components/DiscordCta";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Public creator key portal — YouTubers/streamers/media request a free Steam key.
   Inbound requests are consented by nature; every approval is human-reviewed. */

const EASE = [0.16, 1, 0.3, 1];
const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: EASE } });

const inputCls = "w-full bg-[#050505] border border-white/10 px-4 py-3 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue placeholder:text-zinc-600";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const PERKS = [
  { icon: KeyRound, title: "Free Steam key", body: "Full game, yours forever. Approved creators get keys ahead of launch so coverage is ready for day one." },
  { icon: Gamepad2, title: "Early access build", body: "Play Bornwood before the public — Living Citizens AI, real police AI, CAD, phone OS, heists, the works." },
  { icon: FileText, title: "Full press kit", body: "Logos, screenshots, key art and the fact sheet — everything a video or article needs, one zip." },
  { icon: Sparkles, title: "Direct line to the founder", body: "Interviews, b-roll, feature walkthroughs, custom shots for your thumbnail — just ask." },
];

export default function CreatorKeysPage() {
  const [form, setForm] = useState({ name: "", email: "", channel_url: "", platform: "youtube", subs: "", message: "", consent_updates: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/creator-keys/request", {
        name: form.name.trim(),
        email: form.email.trim(),
        channel_url: form.channel_url.trim(),
        platform: form.platform,
        subs: parseInt(form.subs || "0", 10) || 0,
        message: form.message.trim(),
        consent_updates: form.consent_updates,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="creator-keys-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <Seo title="Free Steam Key for Creators — New Citizen" description="YouTubers, streamers and press: request a free New Citizen Steam key and early access build. UE5 open-world roleplay — a living city that never sleeps." />
      <SiteNav />

      {/* hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(46,107,255,0.12),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 py-20 sm:py-28">
          <motion.p {...fade(0)} className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">New Citizen // Creator Program</motion.p>
          <motion.h1 {...fade(0.05)} className="font-sans font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
            Cover the city.<br /><span className="text-nc-blue">The key is on us.</span>
          </motion.h1>
          <motion.p {...fade(0.1)} className="mt-6 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed">
            New Citizen is a standalone Unreal Engine 5 roleplay platform — a living Australian city with AI citizens who remember you,
            AI police who actually patrol, a full phone OS, CAD/MDT, casino, stock exchange and heists. If you make content about
            FiveM, GTA RP, GMod RP, Arma RP, Roblox RP or emergency-sim games, we want you in first.
          </motion.p>
          <motion.div {...fade(0.15)} className="mt-8 flex flex-wrap gap-3">
            <a href="#request" data-testid="hero-request-key" className="bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors flex items-center gap-2">
              Request your key <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/presskit" data-testid="hero-presskit" className="border border-white/20 text-zinc-200 hover:border-white/40 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors flex items-center gap-2">
              Press kit <FileText className="w-4 h-4" />
            </Link>
            <div className="w-full sm:max-w-sm mt-2 sm:mt-0"><DiscordCta testId="keys-discord" compact /></div>
          </motion.div>
        </div>
      </section>

      {/* perks */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {PERKS.map((p, i) => (
            <motion.div key={p.title} {...fade(i * 0.05)} className="bg-[#050505] p-8">
              <p.icon className="w-5 h-5 text-nc-blue" />
              <h3 className="font-sans font-bold uppercase tracking-widest text-sm text-zinc-100 mt-4">{p.title}</h3>
              <p className="font-mono text-xs text-zinc-500 leading-relaxed mt-2">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* request form */}
      <section id="request" className="max-w-3xl mx-auto px-6 pb-24">
        {done ? (
          <div data-testid="keyreq-success" className="border border-nc-green/40 bg-nc-green/5 p-10 text-center">
            <Check className="w-10 h-10 text-nc-green mx-auto" />
            <h2 className="font-sans font-bold uppercase tracking-widest text-xl text-white mt-4">Request received</h2>
            <p className="font-mono text-xs text-zinc-400 leading-relaxed mt-3 max-w-md mx-auto">
              Confirmation is on its way to your inbox. Every channel is reviewed by a human — approvals usually land within 48 hours.
              Approved creators get their key by email ahead of launch.
            </p>
            <Link to="/presskit" className="inline-flex items-center gap-2 mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue hover:text-white transition-colors">
              Grab the press kit while you wait <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <motion.form {...fade(0)} data-testid="keyreq-form" onSubmit={submit} className="border border-white/10 bg-black/40 p-8 sm:p-10 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <Youtube className="w-5 h-5 text-nc-orange" />
              <h2 className="font-sans font-bold uppercase tracking-widest text-lg text-white">Request your free key</h2>
            </div>
            <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
              Channels with 2,000+ subscribers covering roleplay / simulation games get priority, but every genuine creator is welcome to apply —
              we review each request by hand.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className={labelCls}>Your name / channel name *</label>
                <input data-testid="keyreq-name" required minLength={2} className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Bornwood Bandit" /></div>
              <div><label className={labelCls}>Business email *</label>
                <input data-testid="keyreq-email" required type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@yourchannel.com" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Channel URL *</label>
                <input data-testid="keyreq-channel" required type="url" className={inputCls} value={form.channel_url} onChange={(e) => set("channel_url", e.target.value)} placeholder="https://youtube.com/@yourchannel" /></div>
              <div><label className={labelCls}>Platform</label>
                <select data-testid="keyreq-platform" className={inputCls} value={form.platform} onChange={(e) => set("platform", e.target.value)}>
                  <option value="youtube">YouTube</option><option value="twitch">Twitch</option><option value="tiktok">TikTok</option><option value="media">Media / press</option><option value="other">Other</option>
                </select></div>
              <div><label className={labelCls}>Subscribers / followers</label>
                <input data-testid="keyreq-subs" type="number" min="0" className={inputCls} value={form.subs} onChange={(e) => set("subs", e.target.value)} placeholder="e.g. 5000" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>What kind of coverage are you planning? (optional)</label>
                <textarea data-testid="keyreq-message" rows={3} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="First-look video, stream series, review, server community launch…" /></div>
            </div>
            <label data-testid="keyreq-consent" className="flex items-start gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={form.consent_updates} onChange={(e) => set("consent_updates", e.target.checked)} className="mt-0.5 accent-[#2E6BFF]" />
              <span className="font-mono text-[11px] text-zinc-400 leading-relaxed">
                Email me New Citizen release updates and creator news (optional — unsubscribe any time, one click).
              </span>
            </label>
            <button data-testid="keyreq-submit" type="submit" disabled={busy} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {busy ? "Sending…" : "Request my key"}
            </button>
            <p className="font-mono text-[9px] text-zinc-600 leading-relaxed flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> Reviewed by a human. No spam, ever — Specpixel Entertainment PTY LTD, Melbourne, Australia.
            </p>
          </motion.form>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
